import { MenuItem } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import {
  InvestmentFormFields,
  InvestmentFormFieldsOptional,
  useInvestmentEditForm,
} from "features/investments/InvestmentForm";
import { formatInvestmentLocationCountry } from "features/investments/InvestmentUtils";
import { InvestmentSetupTabProps } from "features/investments/setup/InvestmentSetupTabProps";
import {
  getInvestmentTableDataRetrieveQueryKey,
  useInvestmentRetrieveSuspense,
} from "gen/endpoints/investment/investment";
import { PermissionTypeEnum } from "gen/model/permissionTypeEnum";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import { Restricted } from "shared/components/access/Restricted";
import {
  ContentBlock,
  ContentBlockActions,
  ContentBlockBody,
  ContentBlockGroup,
  ContentBlockHeader,
  ContentBlockMenu,
  ContentBlockTitle,
} from "shared/components/content/ContentBlock";
import Descriptions from "shared/components/descriptions/Descriptions";
import { FormDrawer } from "shared/components/form/FormDrawer";
import { PageTabBody } from "shared/components/layout/PageTabBody";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { formatIsoDateToDisplay } from "shared/utils/DateUtils";
import { formatMoney } from "shared/utils/MoneyUtils";

type InvestmentProfileSection = "basic" | "additional";

export const InvestmentSetupTabProfile: React.FC = () => {
  const { t } = useTranslation(["investment", "common", "entity", "enums"]);
  const queryClient = useQueryClient();

  const { fundFamilyId } = useRequiredStringParams();
  const { investmentId } = useOutletContext<InvestmentSetupTabProps>();

  const { data: investmentData, refetch } = useInvestmentRetrieveSuspense(
    fundFamilyId,
    investmentId,
  );

  const [editingSection, setEditingSection] =
    useState<InvestmentProfileSection>();

  const onCloseComplete = async () => {
    setEditingSection(undefined);
    await refetch();
    await queryClient.invalidateQueries({
      queryKey: getInvestmentTableDataRetrieveQueryKey(fundFamilyId),
    });
  };

  return (
    <PageTabBody data-private={true}>
      <ContentBlockGroup>
        <ContentBlock>
          <ContentBlockHeader>
            <ContentBlockActions>
              <Restricted permission={PermissionTypeEnum.config}>
                <ContentBlockMenu>
                  <MenuItem
                    onClick={() => {
                      setEditingSection("basic");
                    }}
                  >
                    {t("common:edit")}
                  </MenuItem>
                </ContentBlockMenu>
              </Restricted>
            </ContentBlockActions>
          </ContentBlockHeader>
          <ContentBlockBody variant="descriptions">
            <Descriptions>
              <Descriptions.Item label={t("investment:investmentName.short")}>
                {investmentData.short_name}
              </Descriptions.Item>
              <Descriptions.Item label={t("entity:externalId")}>
                {investmentData.external_id}
              </Descriptions.Item>
              <Descriptions.Item label={t("investment:investmentType")}>
                {t(
                  `enums:InvestmentTypeEnum.${investmentData.metadata.investment_type}`,
                )}
              </Descriptions.Item>
              <Descriptions.Item label={t("entity:legalType")}>
                {t(`enums:EntityLegalTypeEnum.${investmentData.legal_type}`)}
              </Descriptions.Item>
              <Descriptions.Item label={t("investment:portfolio")}>
                {investmentData.portfolio_legal_name}
              </Descriptions.Item>
            </Descriptions>
          </ContentBlockBody>
        </ContentBlock>
        <ContentBlock>
          <ContentBlockHeader>
            <ContentBlockTitle>
              {t("common:additionalInformation")}
            </ContentBlockTitle>
            <ContentBlockActions>
              <Restricted permission={PermissionTypeEnum.config}>
                <ContentBlockMenu>
                  <MenuItem
                    onClick={() => {
                      setEditingSection("additional");
                    }}
                  >
                    {t("common:edit")}
                  </MenuItem>
                </ContentBlockMenu>
              </Restricted>
            </ContentBlockActions>
          </ContentBlockHeader>
          <ContentBlockBody variant="descriptions">
            <Descriptions>
              <Descriptions.Item label={t("investment:acquisitionDate")}>
                {formatIsoDateToDisplay(
                  investmentData.metadata.acquisition_date,
                )}
              </Descriptions.Item>
              <Descriptions.Item label={t("investment:dispositionDate")}>
                {formatIsoDateToDisplay(
                  investmentData.metadata.disposition_date,
                )}
              </Descriptions.Item>
              <Descriptions.Item label={t("investment:investmentLocation")}>
                {formatInvestmentLocationCountry(
                  t,
                  investmentData.metadata.investment_location?.country,
                )}
              </Descriptions.Item>
              <Descriptions.Item label={t("investment:targetAmount")}>
                {formatMoney(investmentData.metadata.target_amount)}
              </Descriptions.Item>
              <Descriptions.Item label={t("investment:description")}>
                {investmentData.metadata.description}
              </Descriptions.Item>
            </Descriptions>
          </ContentBlockBody>
        </ContentBlock>
      </ContentBlockGroup>
      {editingSection && (
        <InvestmentEditDrawer
          fundFamilyId={fundFamilyId}
          investmentId={investmentId}
          onCloseComplete={onCloseComplete}
          section={editingSection}
        />
      )}
    </PageTabBody>
  );
};

interface InvestmentEditDrawerProps {
  fundFamilyId: string;
  investmentId: string;
  onCloseComplete: () => void;
  section: InvestmentProfileSection;
}

const InvestmentEditDrawer: React.FC<InvestmentEditDrawerProps> = ({
  fundFamilyId,
  investmentId,
  onCloseComplete,
  section,
}) => {
  const { t } = useTranslation(["investment", "common"]);

  const formProps = useInvestmentEditForm({
    fundFamilyId,
    investmentId,
  });

  return (
    <FormDrawer
      title={t("investment:edit.title")}
      formProps={formProps}
      bodyProps={{
        "data-private": true,
      }}
      onCloseComplete={onCloseComplete}
    >
      {section === "basic" && (
        <InvestmentFormFields fundFamilyId={fundFamilyId} />
      )}
      {section === "additional" && <InvestmentFormFieldsOptional />}
    </FormDrawer>
  );
};
